import type { ReactNode } from 'react';
import { useId, useState } from 'react';

import type {
  StripeElementClasses,
  StripeElementStyle,
} from '@stripe/stripe-js';
import classNames from 'classnames';

import styles from './StyledStripeInput.module.css';

import { useElementState } from '../../../hooks/useElementState';
import { Color } from '../../../styles';
import type { FieldLabelBase } from '../../FieldLabel';
import { FieldLabel } from '../../FieldLabel';
import { FormControl } from '../../FormControl';
import type {
  StripeChangeEvent,
  StripeInputProps,
  SupportedStripeElementType,
} from '../StripeInput';
import { StripeInput } from '../StripeInput/StripeInput';

export const stripeElementClasses: StripeElementClasses = {
  base: 'NOOP',
  complete: 'NOOP',
  empty: 'NOOP',
  focus: 'NOOP',
  invalid: 'NOOP',
};

export const stripeElementStyle: StripeElementStyle = {
  base: {
    color: Color.Kale3,
    fontWeight: '400',
    fontFamily: '"neue-haas-grotesk-display", serif',
    fontSize: '16px',
    letterSpacing: '0.5px',
  },
  invalid: {
    color: Color.ErrorRed,
  },
};

export interface StyledCreditCardInputProps<
  TType extends SupportedStripeElementType,
>
  extends
    FieldLabelBase,
    Omit<
      StripeInputProps<TType>,
      'id' | 'className' | 'style' | 'classes' | 'options'
    > {
  /** Toggle disabled input state */
  disabled?: boolean;
  /** Toggle invalid input state */
  invalid?: boolean;
  /** Container element id */
  id?: string;
  /** An error message to make the field invalid. */
  errorMessage?: string;
  /** CSS class for the container. */
  className?: string;
  /** CSS class for the input element. */
  inputClassName?: string;
  children?: ReactNode | ReactNode[];
}

/** Render a {@link StripeInput} applying the CORGI design. */
export function StyledStripeInput<TType extends SupportedStripeElementType>(
  props: StyledCreditCardInputProps<TType>
) {
  const reactId = useId();
  const id = props.id || reactId;

  const [stripeChangeEvent, setStripeChangeEvent] =
    useState<StripeChangeEvent<TType>>();
  const errorMessage = props.errorMessage || stripeChangeEvent?.error?.message;

  const [state, handlers] = useElementState({
    invalid: props.invalid || Boolean(errorMessage),
    disabled: props.disabled,
  });

  const handleChange: StripeInputProps<TType>['onChange'] = (e, element) => {
    setStripeChangeEvent(e);
    props.onChange?.(e, element);
  };
  const handleBlur: StripeInputProps<TType>['onBlur'] = element => {
    handlers.onBlur(element);
    props.onBlur?.(element);
  };

  const handleFocus: StripeInputProps<TType>['onFocus'] = element => {
    handlers.onFocus(element);
    props.onFocus?.(element);
  };

  return (
    <FormControl
      className={props.className}
      aria-live="polite"
      invalid={state === 'invalid'}
      message={errorMessage}
      id={id}
      aria-describedby={undefined} // Stripe doesn't allow passing of aria-describedby into the iframe. This may be supported in the future
    >
      <FieldLabel
        in={Boolean(state === 'focus' || stripeChangeEvent?.empty === false)}
        label={props.label}
        state={state}
        hideLabel={props.hideLabel}
      >
        <div className={styles.label}>
          <StripeInput
            {...props}
            type={props.type}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            options={{
              disabled: props.disabled,
              placeholder: '',
              classes: stripeElementClasses,
              style: stripeElementStyle,
            }}
            className={classNames(styles.input, props.inputClassName)}
          />
          {props.children}
        </div>
      </FieldLabel>
    </FormControl>
  );
}
